import { colours } from "@shared/ui.design-system";
import { CheckIcon, CrossIcon } from "@shared/ui.icons";
import { Link, Typography } from "@shared/ui.primitives";
import { formatPrice } from "@shared/utils.pricing";
import { clsx } from "clsx";
import { forwardRef } from "react";

import * as styles from "./ComparisonTable.css";
import type { ComparisonTableProps } from "./ComparisonTable.types";

/**
 * Renders different value types with appropriate formatting and components.
 * Handles booleans (as icons), price objects (with currency), and strings/numbers.
 *
 * @param value - The value to render (boolean, price object, or primitive)
 * @param currencySymbol - Currency symbol for price formatting
 * @returns {JSX.Element} Rendered value component
 */
const renderValue = (
  value: string | number | boolean | { price: number; memberPrice?: number },
  currencySymbol: "£" | "$",
  highlighted?: boolean
) => {
  if (typeof value === "boolean") {
    return (
      <div className={styles.iconWrapper}>
        {value ? (
          <CheckIcon
            width={20}
            height={20}
            aria-label={value ? "Yes" : "No"}
            color={highlighted ? colours.secondary.light : colours.primary.dark}
            className={clsx(highlighted && styles.highlightedIconWrapper)}
          />
        ) : (
          <CrossIcon
            width={20}
            height={20}
            aria-label={value ? "Yes" : "No"}
            color={highlighted ? colours.secondary.light : colours.primary.dark}
            className={clsx(highlighted && styles.highlightedIconWrapper)}
          />
        )}
      </div>
    );
  }

  if (typeof value === "object" && value !== null && "price" in value) {
    return (
      <div className={styles.priceContainer}>
        <Typography
          variant={"body-sm-bold"}
          colour="primary-dark"
          textAlign="center"
        >
          {`${formatPrice(value.price, currencySymbol)}`}
        </Typography>
        {value.memberPrice && (
          <div className={styles.memberPriceContainer}>
            <Typography
              as="span"
              variant="smallprint-md"
              colour="primary-medium"
              textAlign="center"
            >
              Members
            </Typography>
            <Typography
              as="span"
              variant="body-sm-bold"
              colour="primary-dark"
              textAlign="center"
            >
              {`${formatPrice(value.memberPrice, currencySymbol)}`}
            </Typography>
          </div>
        )}
      </div>
    );
  }

  return (
    <Typography variant="body-sm-bold" colour="primary-dark">
      {value}
    </Typography>
  );
};

/**
 * Checks if a value is a price object.
 *
 * @param value - The value to check
 * @returns {boolean} True if the value is a price object
 */
const isPrice = (
  value: string | number | boolean | { price: number; memberPrice?: number }
): boolean => {
  return typeof value === "object" && value !== null && "price" in value;
};

/**
 * Determines the appropriate CSS class name for table cells based on highlighting, position, and content type.
 *
 * @param isHighlighted - Whether the column is highlighted (pink background)
 * @param isLastRow - Whether this is the last row (affects border radius)
 * @param isPriceValue - Whether the cell contains price data (affects vertical alignment)
 * @returns {string} CSS class name or combination of class names
 */
const getCellClassName = (
  isHighlighted: boolean,
  isLastRow: boolean,
  isPriceValue: boolean = false
) => {
  if (isPriceValue) {
    if (isLastRow && isHighlighted) return styles.lastRowHighlightedPriceCell;
    if (isLastRow) return styles.lastRowPriceBodyCell;
    if (isHighlighted) return styles.highlightedPriceBodyCell;
    return styles.priceBodyCell;
  }

  if (isLastRow && isHighlighted) return styles.lastRowHighlightedCell;
  if (isLastRow) return clsx(styles.bodyCell, styles.lastRowBodyCell);
  if (isHighlighted) return styles.highlightedBodyCell;
  return styles.bodyCell;
};

/**
 * A comparison table component for displaying product features side-by-side.
 * Features sticky first column, highlighted columns, accessibility support, and responsive design with scroll indicators.
 *
 * @param data - The comparison data containing columns and features to display
 * @param title - Optional title displayed above the table using Typography subheader-4
 * @param dataTestId - Optional test ID for testing purposes, applied to the main container
 * @param currencySymbol - Currency symbol for price formatting
 * @param ref - Forward ref to the main container div for external access
 * @returns {JSX.Element | null} The comparison table component or null if no data
 *
 * @example
 * ```tsx
 * const comparisonData = {
 *   product: "Mascara",
 *   slug: "mascara",
 *   columns: [
 *     { name: "Beauty Pie", highlighted: true },
 *     { name: "Competitor" }
 *   ],
 *   features: [
 *     {
 *       name: "Price",
 *       values: {
 *         "Beauty Pie": { price: 28, memberPrice: 14 },
 *         "Competitor": { price: 35 }
 *       }
 *     }
 *   ]
 * };
 *
 * <ComparisonTable
 *   data={comparisonData}
 *   title="Product Comparison"
 *   currencySymbol="£"
 *   dataTestId="comparison-table"
 * />
 * ```
 */
export const ComparisonTable = forwardRef<
  HTMLTableElement,
  ComparisonTableProps
>(
  (
    { data, title, dataTestId, currencySymbol, scrollableRef, footerCTA },
    ref
  ) => {
    if (!data.features.length || !data.columns.length) {
      return null;
    }

    return (
      <div>
        {title && (
          <div className={styles.titleContainer}>
            <Typography as="h3" variant="subheader-3" colour="primary-dark">
              {title}
            </Typography>
          </div>
        )}
        <div className={styles.tableWrapper}>
          <div
            className={styles.tableContainer}
            tabIndex={0}
            role="region"
            aria-label="Scrollable comparison table"
            ref={scrollableRef}
          >
            <table
              ref={ref}
              className={styles.table}
              role="table"
              aria-label={title || "Product comparison table"}
              data-testid={dataTestId}
            >
              <caption className={styles.srOnly}>
                {title || "Product comparison data"}
              </caption>
              <thead>
                <tr className={styles.headerRow}>
                  <th className={styles.stickyHeaderCell} scope="col">
                    <span className={styles.srOnly}>Features</span>
                  </th>
                  {data.columns.map((column) => (
                    <th
                      key={column.name}
                      scope="col"
                      className={
                        column.highlighted
                          ? styles.highlightedHeaderCell
                          : styles.headerCell
                      }
                    >
                      <Typography
                        variant="smallprint-md-bold"
                        colour="primary-dark"
                        textAlign="center"
                      >
                        {column.name}
                      </Typography>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.features.map((feature, index) => {
                  const isLastRow = index === data.features.length - 1;
                  return (
                    <tr key={feature.name} className={styles.bodyRow}>
                      <th
                        className={clsx(
                          styles.stickyBodyCell,
                          isLastRow && styles.lastStickyBodyCell
                        )}
                        scope="row"
                      >
                        <Typography
                          variant="smallprint-md-bold"
                          colour="primary-dark"
                        >
                          {feature.name}
                        </Typography>
                      </th>
                      {data.columns.map((column) => {
                        const cellValue = feature.values[column.name];
                        return (
                          <td
                            key={column.name}
                            className={getCellClassName(
                              column.highlighted || false,
                              isLastRow,
                              isPrice(cellValue)
                            )}
                          >
                            {renderValue(
                              cellValue,
                              currencySymbol,
                              column.highlighted
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {footerCTA && (
            <div className={styles.footerContainer}>
              <Link
                noPadding
                width="minContent"
                variant="secondary"
                href={footerCTA.href}
                aria-label={footerCTA.altText || footerCTA.copy}
              >
                <Typography
                  variant="button-sm"
                  colour="primary-dark"
                >
                  {footerCTA.copy}
                </Typography>
              </Link>
            </div>
          )}
        </div>
      </div>
    );
  }
);
